import React, { useRef, useState } from "react";
import instagramPosts, { INSTAGRAM_PROFILE } from "../data/instagramFeed";
import "../styles/InstagramCarousel.css";

const DRAG_THRESHOLD = 6;

const InstagramCarousel = () => {
  const trackRef = useRef(null);
  const dragRef = useRef({ startX: 0, scrollLeft: 0, moved: false, pointerId: null });
  const [isDragging, setIsDragging] = useState(false);

  const getScrollStep = () => {
    const el = trackRef.current;
    if (!el) return 280;
    const card = el.querySelector(".instagram-carousel-card");
    const cardWidth = card ? card.getBoundingClientRect().width : 260;
    const styles = window.getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap) || 20;
    return cardWidth + gap;
  };

  const scrollByStep = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const step = getScrollStep();
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  const handlePointerDown = (e) => {
    const el = trackRef.current;
    if (!el || e.button > 0) return;

    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      moved: false,
      pointerId: e.pointerId,
    };

    el.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const el = trackRef.current;
    if (!el) return;

    const dx = e.clientX - dragRef.current.startX;
    if (Math.abs(dx) > DRAG_THRESHOLD) {
      dragRef.current.moved = true;
    }

    el.scrollLeft = dragRef.current.scrollLeft - dx;
  };

  const endDrag = () => {
    const el = trackRef.current;
    if (!el || !isDragging) return;

    if (dragRef.current.pointerId != null) {
      try {
        el.releasePointerCapture(dragRef.current.pointerId);
      } catch {
        /* already released */
      }
    }

    setIsDragging(false);
    dragRef.current.pointerId = null;
  };

  const handleCardClick = (e) => {
    if (dragRef.current.moved) {
      e.preventDefault();
      dragRef.current.moved = false;
    }
  };

  const handleCardKeyDown = (e, url) => {
    if (e.key === "Enter" || e.key === " ") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  if (!instagramPosts.length) return null;

  return (
    <section className="instagram-carousel-section" aria-label="Instagram highlights">
      <div className="instagram-carousel-header">
        <h2 className="instagram-carousel-title">From Our Instagram</h2>
        <p className="instagram-carousel-subtitle">
          Moments from performances, classes, and temple programs — swipe to browse or tap a post to open
        </p>
        <a
          href={INSTAGRAM_PROFILE}
          target="_blank"
          rel="noopener noreferrer"
          className="instagram-follow-link"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
          Follow on Instagram
        </a>
        <p className="instagram-carousel-hint">Swipe, drag, or use arrows to browse · Tap a post to view on Instagram</p>
      </div>

      <div className="instagram-carousel-wrapper">
        <button
          type="button"
          className="instagram-carousel-nav instagram-carousel-nav-left"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            scrollByStep(-1);
          }}
          aria-label="Scroll Instagram posts left"
        >
          ‹
        </button>

        <div className="instagram-carousel-fade instagram-carousel-fade-left" aria-hidden="true" />
        <div className="instagram-carousel-fade instagram-carousel-fade-right" aria-hidden="true" />

        <div
          ref={trackRef}
          className={`instagram-carousel-track${isDragging ? " is-dragging" : ""}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {instagramPosts.map((post, index) => (
            <a
              key={`${post.image}-${index}`}
              href={post.url}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-carousel-card"
              aria-label={`View on Instagram: ${post.alt}`}
              onClick={handleCardClick}
              onKeyDown={(e) => handleCardKeyDown(e, post.url)}
              draggable={false}
            >
              <img src={post.imageSrc} alt={post.alt} loading="lazy" draggable={false} />
              <span className="instagram-card-overlay">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </span>
            </a>
          ))}
        </div>

        <button
          type="button"
          className="instagram-carousel-nav instagram-carousel-nav-right"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            scrollByStep(1);
          }}
          aria-label="Scroll Instagram posts right"
        >
          ›
        </button>
      </div>
    </section>
  );
};

export default InstagramCarousel;
